// ===========================================
// PIXI REGISTRY - ROTATION UTILITIES
// ===========================================
// Shared rotation and trigonometric functions
// used across animation, reconciliation, and resync code.
// ===========================================

import { BEAM_ROTATIONS, BEAM_HIDDEN_ROTATION } from './constants';

// ===========================================
// DEGREE/RADIAN CONVERSION
// ===========================================

/** Convert degrees to radians */
export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Convert radians to degrees */
export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

// ===========================================
// ROTATION TRANSFORMS
// ===========================================

/**
 * Apply a radial offset using rotation.
 * Converts a local offset along the seat's radial axis to world coordinates.
 *
 * @param baseX - Base X position
 * @param baseY - Base Y position
 * @param radialOffset - Distance along radial axis (negative = toward dealer)
 * @param rotationRad - Rotation in radians
 * @returns World coordinates after applying rotated offset
 */
export function applyRadialOffset(
  baseX: number,
  baseY: number,
  radialOffset: number,
  rotationRad: number
): { x: number; y: number } {
  return {
    x: baseX + radialOffset * -Math.sin(rotationRad),
    y: baseY + radialOffset * Math.cos(rotationRad),
  };
}

/**
 * Apply a tangent offset using rotation.
 * Moves position perpendicular to the radial axis (left/right relative to seat).
 *
 * @param position - Base position { x, y }
 * @param tangentOffset - Distance along tangent axis (positive = right)
 * @param rotationRad - Rotation in radians
 * @returns World coordinates after applying tangent offset
 */
export function applyTangentOffset(
  position: { x: number; y: number },
  tangentOffset: number,
  rotationRad: number
): { x: number; y: number } {
  return {
    x: position.x + tangentOffset * Math.cos(rotationRad),
    y: position.y + tangentOffset * Math.sin(rotationRad),
  };
}

/**
 * Apply both radial and tangent offsets to a position.
 * Useful for complex positioning that needs both axes.
 *
 * @param baseX - Base X position
 * @param baseY - Base Y position
 * @param radialOffset - Distance along radial axis
 * @param tangentOffset - Distance along tangent axis
 * @param rotationRad - Rotation in radians
 * @returns World coordinates after applying both offsets
 */
export function applyRotatedOffsets(
  baseX: number,
  baseY: number,
  radialOffset: number,
  tangentOffset: number,
  rotationRad: number
): { x: number; y: number } {
  return {
    x: baseX + radialOffset * -Math.sin(rotationRad) + tangentOffset * Math.cos(rotationRad),
    y: baseY + radialOffset * Math.cos(rotationRad) + tangentOffset * Math.sin(rotationRad),
  };
}

/**
 * Transform local coordinates to world coordinates using seat rotation.
 * Used for positioning cards relative to seat center.
 *
 * @param seatX - Seat center X
 * @param seatY - Seat center Y
 * @param localX - Local X offset (along card row)
 * @param localY - Local Y offset (perpendicular to card row, toward dealer)
 * @param rotationRad - Seat rotation in radians
 * @returns World coordinates
 */
export function localToWorld(
  seatX: number,
  seatY: number,
  localX: number,
  localY: number,
  rotationRad: number
): { x: number; y: number } {
  return {
    x: seatX + localX * Math.cos(rotationRad) + localY * -Math.sin(rotationRad),
    y: seatY + localX * Math.sin(rotationRad) + localY * Math.cos(rotationRad),
  };
}

// ===========================================
// BEAM ROTATION
// ===========================================

/**
 * Get the beam rotation angle for a given visual seat position.
 *
 * @param visualPosition - Visual seat position (1-7)
 * @returns Rotation angle in degrees
 */
export function getBeamRotationForSeat(visualPosition: number): number {
  return BEAM_ROTATIONS[visualPosition] ?? BEAM_HIDDEN_ROTATION;
}

/**
 * Get the hidden beam rotation (pointing down).
 *
 * @returns Hidden rotation angle in degrees
 */
export function getBeamHiddenRotation(): number {
  return BEAM_HIDDEN_ROTATION;
}

// ===========================================
// ANGLE UTILITIES
// ===========================================

/**
 * Normalize an angle to the range [0, 360).
 *
 * @param degrees - Angle in degrees
 * @returns Normalized angle in range [0, 360)
 */
export function normalizeAngle(degrees: number): number {
  let normalized = degrees % 360;
  if (normalized < 0) normalized += 360;
  return normalized;
}

/**
 * Calculate the shortest angular distance between two angles.
 * Handles wrapping around 360 degrees.
 *
 * @param from - Starting angle in degrees
 * @param to - Target angle in degrees
 * @returns Signed angular distance (negative = counterclockwise)
 */
export function shortestAngleDiff(from: number, to: number): number {
  let diff = to - from;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}
